import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Container, Grid, Box, Button } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';

import { onFetchBlogs } from '../../store/blogs/action';
import BackdropLoader from '../../components/loading/BackdropLoader';
import BlogCard from './BlogCard';
import { blogCategoryFilter, sortByKey } from '../../hooks/utilsFn/utilsFn';
import NoBlogsFound from './NoBlogsFound';

const PER_PAGE = 9; // blogs per page

const Blogpage = () => {
  const blogsData = useSelector((state) => state.blogs?.allData);
  const isLoading = useSelector((state) => state.blogs?.loading);

  const [filterLoading, setFilterLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const dispatch = useDispatch();

  const [filteredBlogs, setFilteredBlogs] = useState({
    isFilter: false,
    data: [],
    isDataFound: true,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const canonicalUrl = useMemo(
    () => `https://www.99gift.in${location.pathname}`,
    [location.pathname]
  );

  const dataFilter = async (data, category) => {
    const filtered = await blogCategoryFilter(data, 'categroies', category);
    if (!filtered?.length) {
      setFilteredBlogs({ isFilter: true, data: [], isDataFound: false });
    } else {
      const sortedData = sortByKey(filtered, 'updated_at', 'desc');
      setFilteredBlogs({ isFilter: true, data: sortedData, isDataFound: true });
    }
    setFilterLoading(false);
  };

  useEffect(() => {
    if (!searchParams?.size) {
      setFilteredBlogs({ isFilter: false, data: [], isDataFound: true });
      setFilterLoading(false);
    } else {
      setFilterLoading(true);
      const category = searchParams.get('category');
      dataFilter(blogsData, category);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!blogsData || blogsData.length === 0) {
      dispatch(onFetchBlogs());
    }
  }, [dispatch, blogsData]);

  const displayedBlogs = filteredBlogs?.isFilter
    ? filteredBlogs?.data?.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE)
    : blogsData?.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const totalPages = Math.ceil(
    (filteredBlogs?.isFilter ? filteredBlogs?.data?.length : blogsData?.length) / PER_PAGE
  );

  return (
    <>
      <BlogMeta canonicalUrl={canonicalUrl} />

      <Container
        maxWidth="xl"
        sx={{
          backgroundColor: 'white.main',
          mt: { xs: 2, sm: 4 },
          mb: { md: 4 },
          minHeight: { lg: 400 },
        }}
      >
        {isLoading || filterLoading ? (
          <BackdropLoader open={isLoading || filterLoading} />
        ) : (
          <Grid container columnSpacing={4}>
            <Grid item xs={12} md={12}>
              <Grid container rowSpacing={3} columnSpacing={3}>
                {displayedBlogs?.length > 0 ? (
                  displayedBlogs.map((item) => <BlogCard data={item} key={item.id} />)
                ) : (
                  <NoBlogsFound />
                )}
              </Grid>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 1 }}>
                  <Button
                    variant="outlined"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => prev - 1)}
                  >
                    Prev
                  </Button>
                  {[...Array(totalPages)].map((_, idx) => (
                    <Button
                      key={idx}
                      variant={currentPage === idx + 1 ? 'contained' : 'outlined'}
                      onClick={() => setCurrentPage(idx + 1)}
                    >
                      {idx + 1}
                    </Button>
                  ))}
                  <Button
                    variant="outlined"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        )}
      </Container>
    </>
  );
};

export default Blogpage;

const BlogMeta = ({ canonicalUrl }) => (
  <Helmet>
    <title>Blogs Regarding Gift Cards - 99gift</title>
    <meta name="description" content="Blogs Regarding Gift Cards - 99gift" />
    <meta property="og:url" content={canonicalUrl} />
    <meta property="og:title" content="Blogs Regarding Gift Cards - 99gift" />
    <meta property="og:description" content="Blogs Regarding Gift Cards - 99gift" />
    <link rel="canonical" href={canonicalUrl} />
  </Helmet>
);
